import { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./ClinicStudentsPage.module.css";

const AVATAR_PALETTE = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms  = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return new Date(iso).toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24) return `${hr}h ago`;
  const d   = Math.floor(hr / 24);
  if (d   < 7)  return `${d}d ago`;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return new Date(iso).toLocaleString(); }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

const SEVERITY_OPTIONS = ["MILD", "MODERATE", "SEVERE"];

export default function ClinicStudentsPage() {
  const [students, setStudents]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [search, setSearch]       = useState("");
  const [filter, setFilter]       = useState("all"); // "all" | "withAllergies" | "onMed"
  const [selectedId, setSelectedId] = useState(null);

  // Per-student clinic data cache so flipping between students is
  // instant after the first load.
  const [profileCache, setProfileCache] = useState({}); // {studentId: {visits, meds, allergies, loading, error}}

  // Lightweight summary of which students have allergies / severe
  // allergies / active medication — drives the filter chips AND the
  // emergency-allergy badge on list rows. Pre-loaded once with the
  // students list so the filters work the moment the page renders.
  // Sets for O(1) membership checks.
  const [summary, setSummary] = useState({
    withAllergies: new Set(),
    withSevereAllergies: new Set(),
    onMedication: new Set(),
  });

  // Fetch the students roster AND the clinic summary in parallel.
  // Both are scoped to clinic-role callers on the server. The page
  // is route-gated identically.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/students?all=true`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/clinic/students-summary`).then(r => r.ok ? r.json() : null).catch(() => null),
    ])
      .then(([list, sum]) => {
        if (cancelled) return;
        setStudents(Array.isArray(list) ? list : []);
        if (sum) {
          setSummary({
            withAllergies:       new Set((sum.withAllergies       || []).map(Number)),
            withSevereAllergies: new Set((sum.withSevereAllergies || []).map(Number)),
            onMedication:        new Set((sum.onMedication        || []).map(Number)),
          });
        }
        setLoading(false);
      })
      .catch(e => {
        if (!cancelled) { setError(e.message || "Failed to load"); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, []);

  /** Refresh the summary after an in-detail mutation so the filter
   *  chips and badges stay accurate. */
  const refreshSummary = useCallback(() => {
    fetch(`${API_BASE}/api/clinic/students-summary`)
      .then(r => r.ok ? r.json() : null)
      .then(sum => {
        if (!sum) return;
        setSummary({
          withAllergies:       new Set((sum.withAllergies       || []).map(Number)),
          withSevereAllergies: new Set((sum.withSevereAllergies || []).map(Number)),
          onMedication:        new Set((sum.onMedication        || []).map(Number)),
        });
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  /** Lazy-load a student's clinic profile (visits + meds + allergies)
   *  the first time their row is clicked. Three parallel fetches; all
   *  three endpoints are clinic-role-gated on the server. */
  const loadProfile = useCallback((studentId) => {
    if (!studentId) return;
    setProfileCache(prev => {
      // Already cached? Bail.
      if (prev[studentId] && !prev[studentId].error) return prev;
      return { ...prev, [studentId]: { loading: true, visits: [], meds: [], allergies: [] } };
    });
    const safe = (promise, fb) => promise.then(r => r.ok ? r.json() : fb).catch(() => fb);
    Promise.all([
      safe(fetch(`${API_BASE}/api/clinic/visits?studentId=${studentId}`), []),
      safe(fetch(`${API_BASE}/api/clinic/medications?studentId=${studentId}`), []),
      safe(fetch(`${API_BASE}/api/clinic/allergies?studentId=${studentId}`), []),
    ]).then(([visits, meds, allergies]) => {
      setProfileCache(prev => ({
        ...prev,
        [studentId]: {
          loading: false,
          visits:    Array.isArray(visits)    ? visits    : [],
          meds:      Array.isArray(meds)      ? meds      : [],
          allergies: Array.isArray(allergies) ? allergies : [],
        },
      }));
    });
  }, []);

  // Whenever the selectedId changes, ensure that student's profile is in cache.
  useEffect(() => {
    if (selectedId) loadProfile(selectedId);
  }, [selectedId, loadProfile]);

  function refreshProfile(studentId) {
    setProfileCache(prev => {
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
    loadProfile(studentId);
    // The mutation may have flipped this student in/out of the
    // summary sets — refresh those too so the chip counts and
    // badges stay honest.
    refreshSummary();
  }

  // Filter / search. Now backed by the server-side summary so the
  // chips work the moment the page renders, not just for students
  // whose profile has been clicked into.
  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students
      .filter(st => {
        if (filter === "all") return true;
        if (filter === "withAllergies") return summary.withAllergies.has(Number(st.id));
        if (filter === "onMed")         return summary.onMedication.has(Number(st.id));
        return true;
      })
      .filter(st => {
        if (!q) return true;
        const full = `${st.firstName || ""} ${st.lastName || ""}`.toLowerCase();
        return full.includes(q) || (st.studentNumber || "").toLowerCase().includes(q);
      })
      .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
  }, [students, search, filter, summary]);

  const selectedStudent = students.find(s => String(s.id) === String(selectedId)) || null;
  const profile = selectedId ? profileCache[selectedId] : null;

  return (
    <div className={s.page}>
      <header className={s.hero}>
        <div className={s.heroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>
          </svg>
        </div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Clinic · Students</div>
          <h1 className={s.heroTitle}>Health profiles</h1>
          <p className={s.heroSub}>
            Every student's medical profile in one place — clinic visits,
            ongoing medication, and allergy alerts. Click a student on
            the left to see their full record on the right.
          </p>
        </div>
      </header>

      <div className={s.layout}>
        {/* ── Left: students list with search + filter ────── */}
        <aside className={s.listCol}>
          <div className={s.filterBar}>
            <div className={s.searchWrap}>
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
                   className={s.searchIcon} aria-hidden="true">
                <circle cx="9" cy="9" r="6"/>
                <path d="M15 15l-3.5-3.5"/>
              </svg>
              <input
                className={s.searchInput}
                type="text"
                placeholder="Search by name or student #…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className={s.filterChips} role="tablist">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
                All ({students.length})
              </FilterChip>
              <FilterChip active={filter === "withAllergies"} onClick={() => setFilter("withAllergies")}>
                With allergies
              </FilterChip>
              <FilterChip active={filter === "onMed"} onClick={() => setFilter("onMed")}>
                On medication
              </FilterChip>
            </div>
          </div>

          {loading ? (
            <div className={s.skel}>
              <div className={s.skelRow} /><div className={s.skelRow} /><div className={s.skelRow} />
            </div>
          ) : error ? (
            <div className={s.errorBox}>{error}</div>
          ) : visibleStudents.length === 0 ? (
            <div className={s.emptyList}>No students match this filter.</div>
          ) : (
            <ul className={s.list}>
              {visibleStudents.map((st, idx) => {
                const fullName = `${st.lastName || ""}, ${st.firstName || ""}`.replace(/^, |, $/g, "").trim();
                const active = String(selectedId) === String(st.id);
                // Badges now come from the summary endpoint — they
                // render on every row from page load, not just rows
                // whose profile has been clicked into.
                const sid = Number(st.id);
                const hasSevereAllergy = summary.withSevereAllergies.has(sid);
                const onActiveMed      = summary.onMedication.has(sid);
                return (
                  <li key={st.id}>
                    <button
                      type="button"
                      className={`${s.studentRow} ${active ? s.studentRowActive : ""}`}
                      style={{ animationDelay: `${Math.min(idx, 12) * 18}ms` }}
                      onClick={() => setSelectedId(st.id)}>
                      <div className={s.studentAvatar}
                           style={{ background: avatarColor(fullName) }}>
                        {initials(st.firstName, st.lastName)}
                      </div>
                      <div className={s.studentBody}>
                        <div className={s.studentName} title={fullName}>{fullName || "Unnamed"}</div>
                        <div className={s.studentMeta}>
                          {st.studentNumber && (
                            <span className={s.studentNumber}>#{st.studentNumber}</span>
                          )}
                          {hasSevereAllergy && (
                            <span className={`${s.flag} ${s.flagDanger}`} title="Severe allergy on file">⚠ Severe allergy</span>
                          )}
                          {onActiveMed && (
                            <span className={`${s.flag} ${s.flagInfo}`} title="On medication">℞ On meds</span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* ── Right: selected student's full clinic profile ── */}
        <main className={s.detailCol}>
          {!selectedStudent ? (
            <div className={s.detailEmpty}>
              <div className={s.detailEmptyIcon} aria-hidden="true">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.6"
                     strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/>
                  <path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2"/>
                </svg>
              </div>
              <div className={s.detailEmptyTitle}>Pick a student to begin</div>
              <div className={s.detailEmptyBody}>
                Their full health profile — visits, current medication,
                and allergy alerts — will appear here.
              </div>
            </div>
          ) : (
            <StudentClinicProfile
              student={selectedStudent}
              profile={profile}
              onRefresh={() => refreshProfile(selectedStudent.id)}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${s.filterChip} ${active ? s.filterChipActive : ""}`}
      onClick={onClick}>
      {children}
    </button>
  );
}

/** Right-side panel — full clinic record for the selected student. */
function StudentClinicProfile({ student, profile, onRefresh }) {
  const fullName = `${student.lastName || ""}, ${student.firstName || ""}`.replace(/^, |, $/g, "").trim();
  const severe = (profile?.allergies || []).filter(a => a.severity === "SEVERE");
  const loading = !profile || profile.loading;

  return (
    <div className={s.profileCard}>
      {/* ── Profile hero ─────────────────────────────────── */}
      <header className={s.profileHero}>
        {student.photoUrl
          ? <img src={student.photoUrl} alt="" className={s.profileAvatarImg} />
          : <div className={s.profileAvatar}
                 style={{ background: avatarColor(fullName) }}>
              {initials(student.firstName, student.lastName)}
            </div>}
        <div className={s.profileIdent}>
          <div className={s.profileEyebrow}>Patient</div>
          <h2 className={s.profileName}>{fullName || "Unnamed"}</h2>
          <div className={s.profileMeta}>
            {student.studentNumber && (
              <span className={s.profilePillMono}>#{student.studentNumber}</span>
            )}
            {student.status && (
              <span className={s.profilePill}>{student.status}</span>
            )}
          </div>
        </div>
        {severe.length > 0 && (
          <div className={s.dangerRibbon} title="Severe allergy on file">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9"  x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12" y2="17.01"/>
            </svg>
            Severe allergy
          </div>
        )}
      </header>

      {/* ── Allergies section ─────────────────────────────── */}
      <AllergiesSection
        studentId={student.id}
        allergies={profile?.allergies || []}
        loading={loading}
        onChanged={onRefresh}
      />

      {/* ── Medications section ───────────────────────────── */}
      <MedicationsSection
        studentId={student.id}
        meds={profile?.meds || []}
        loading={loading}
        onChanged={onRefresh}
      />

      {/* ── Visits section ────────────────────────────────── */}
      <VisitsSection
        visits={profile?.visits || []}
        loading={loading}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Allergies
// ─────────────────────────────────────────────────────────────
function AllergiesSection({ studentId, allergies, loading, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [allergen, setAllergen] = useState("");
  const [severity, setSeverity] = useState("MILD");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function reset() {
    setAllergen(""); setSeverity("MILD"); setNotes(""); setAdding(false); setErr("");
  }

  async function add(e) {
    e.preventDefault();
    if (busy) return;
    if (!allergen.trim()) { setErr("Allergen is required."); return; }
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/clinic/allergies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId, allergen: allergen.trim(), severity, notes: notes.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      reset();
      onChanged();
    } catch (e2) { setErr(e2.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Remove this allergy?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/clinic/allergies/${id}`, { method: "DELETE" });
      if (res.ok) onChanged();
    } catch { /* non-fatal — refresh will heal */ }
  }

  return (
    <section className={s.section}>
      <header className={s.sectionHead}>
        <div>
          <h3 className={s.sectionTitle}>Allergies</h3>
          <p className={s.sectionSub}>
            Severity-coded so emergencies are obvious at a glance.
          </p>
        </div>
        {!adding && (
          <button type="button" className={s.addBtn} onClick={() => setAdding(true)}>
            + Add allergy
          </button>
        )}
      </header>

      {adding && (
        <form className={s.addForm} onSubmit={add}>
          <div className={s.addFormRow}>
            <input
              className={s.input}
              type="text"
              placeholder="Allergen — e.g. peanuts, penicillin"
              value={allergen}
              onChange={e => setAllergen(e.target.value)}
              disabled={busy}
              maxLength={200}
              autoFocus
            />
            <select className={s.select}
                    value={severity}
                    onChange={e => setSeverity(e.target.value)}
                    disabled={busy}>
              {SEVERITY_OPTIONS.map(sev => (
                <option key={sev} value={sev}>{sev}</option>
              ))}
            </select>
          </div>
          <input
            className={s.input}
            type="text"
            placeholder="Reaction or emergency steps (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={busy}
          />
          {err && <div className={s.errorBox}>{err}</div>}
          <div className={s.addFormActions}>
            <button type="button" className={s.btnGhost}
                    onClick={reset} disabled={busy}>Cancel</button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !allergen.trim()}>
              {busy ? "Saving…" : "Save allergy"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className={s.skelMini}><div className={s.skelMiniRow} /></div>
      ) : allergies.length === 0 ? (
        <div className={s.itemsEmpty}>No allergies on file.</div>
      ) : (
        <ul className={s.items}>
          {allergies.map(a => (
            <li key={a.id} className={s.allergyRow}>
              <span className={`${s.severityChip} ${s["severity_" + (a.severity || "MILD").toLowerCase()]}`}>
                {a.severity || "MILD"}
              </span>
              <div className={s.itemBody}>
                <div className={s.itemTitle}>{a.allergen}</div>
                {a.notes && <div className={s.itemNotes}>{a.notes}</div>}
              </div>
              <button type="button" className={s.removeBtn}
                      onClick={() => remove(a.id)}
                      title="Remove">×</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
//  Medications
// ─────────────────────────────────────────────────────────────
function MedicationsSection({ studentId, meds, loading, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [drug, setDrug] = useState("");
  const [dose, setDose] = useState("");
  const [schedule, setSchedule] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [prescribedBy, setPrescribedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function reset() {
    setDrug(""); setDose(""); setSchedule("");
    setStartDate(""); setEndDate(""); setPrescribedBy("");
    setNotes(""); setAdding(false); setErr("");
  }

  async function add(e) {
    e.preventDefault();
    if (busy) return;
    if (!drug.trim()) { setErr("Drug name is required."); return; }
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/clinic/medications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId,
          drugName: drug.trim(),
          dose: dose.trim() || null,
          schedule: schedule.trim() || null,
          startDate: startDate || null,
          endDate: endDate || null,
          prescribedBy: prescribedBy.trim() || null,
          notes: notes.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      reset();
      onChanged();
    } catch (e2) { setErr(e2.message || "Save failed"); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Remove this medication?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/clinic/medications/${id}`, { method: "DELETE" });
      if (res.ok) onChanged();
    } catch { /* non-fatal */ }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <section className={s.section}>
      <header className={s.sectionHead}>
        <div>
          <h3 className={s.sectionTitle}>Medications</h3>
          <p className={s.sectionSub}>
            Active = no end date or end date in the future. Past
            courses stay on file for history.
          </p>
        </div>
        {!adding && (
          <button type="button" className={s.addBtn} onClick={() => setAdding(true)}>
            + Add medication
          </button>
        )}
      </header>

      {adding && (
        <form className={s.addForm} onSubmit={add}>
          <div className={s.addFormRow}>
            <input className={s.input} type="text"
                   placeholder="Drug name — e.g. Paracetamol"
                   value={drug} onChange={e => setDrug(e.target.value)}
                   disabled={busy} maxLength={200} autoFocus />
            <input className={s.input} type="text"
                   placeholder="Dose — e.g. 250mg"
                   value={dose} onChange={e => setDose(e.target.value)}
                   disabled={busy} maxLength={100} />
          </div>
          <input className={s.input} type="text"
                 placeholder="Schedule — Morning + Evening, Every 8 hours"
                 value={schedule} onChange={e => setSchedule(e.target.value)}
                 disabled={busy} maxLength={200} />
          <div className={s.addFormRow}>
            <label className={s.subField}>
              <span>Start date</span>
              <input className={s.input} type="date"
                     value={startDate} onChange={e => setStartDate(e.target.value)}
                     disabled={busy} />
            </label>
            <label className={s.subField}>
              <span>End date (blank = ongoing)</span>
              <input className={s.input} type="date"
                     value={endDate} onChange={e => setEndDate(e.target.value)}
                     disabled={busy} />
            </label>
          </div>
          <input className={s.input} type="text"
                 placeholder="Prescribed by (optional)"
                 value={prescribedBy} onChange={e => setPrescribedBy(e.target.value)}
                 disabled={busy} maxLength={200} />
          <input className={s.input} type="text"
                 placeholder="Notes (optional)"
                 value={notes} onChange={e => setNotes(e.target.value)}
                 disabled={busy} />
          {err && <div className={s.errorBox}>{err}</div>}
          <div className={s.addFormActions}>
            <button type="button" className={s.btnGhost}
                    onClick={reset} disabled={busy}>Cancel</button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !drug.trim()}>
              {busy ? "Saving…" : "Save medication"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className={s.skelMini}><div className={s.skelMiniRow} /></div>
      ) : meds.length === 0 ? (
        <div className={s.itemsEmpty}>No medications on file.</div>
      ) : (
        <ul className={s.items}>
          {meds.map(med => {
            const active = !med.endDate || med.endDate >= today;
            return (
              <li key={med.id} className={`${s.medRow} ${active ? s.medRowActive : s.medRowEnded}`}>
                <div className={s.medIcon} aria-hidden="true">℞</div>
                <div className={s.itemBody}>
                  <div className={s.medTopRow}>
                    <span className={s.itemTitle}>{med.drugName}</span>
                    {med.dose && <span className={s.medDose}>{med.dose}</span>}
                    <span className={`${s.medStatus} ${active ? s.medStatusActive : s.medStatusEnded}`}>
                      {active ? "Active" : "Ended"}
                    </span>
                  </div>
                  <div className={s.medMeta}>
                    {med.schedule && <span>{med.schedule}</span>}
                    {(med.startDate || med.endDate) && (
                      <span>
                        {med.startDate ? fmtDate(med.startDate) : "—"}
                        {" → "}
                        {med.endDate ? fmtDate(med.endDate) : "ongoing"}
                      </span>
                    )}
                    {med.prescribedBy && <span>by {med.prescribedBy}</span>}
                  </div>
                  {med.notes && <div className={s.itemNotes}>{med.notes}</div>}
                </div>
                <button type="button" className={s.removeBtn}
                        onClick={() => remove(med.id)}
                        title="Remove">×</button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
//  Visits (read-only here; logging happens on the dashboard)
// ─────────────────────────────────────────────────────────────
function VisitsSection({ visits, loading }) {
  return (
    <section className={s.section}>
      <header className={s.sectionHead}>
        <div>
          <h3 className={s.sectionTitle}>Visit history</h3>
          <p className={s.sectionSub}>
            Every clinic walk-in for this student. Log new visits from
            the dashboard's <em>New visit</em> button.
          </p>
        </div>
      </header>

      {loading ? (
        <div className={s.skelMini}><div className={s.skelMiniRow} /><div className={s.skelMiniRow} /></div>
      ) : visits.length === 0 ? (
        <div className={s.itemsEmpty}>No clinic visits on record for this student.</div>
      ) : (
        <ul className={s.items}>
          {visits.map(v => {
            const inClinic = !v.returnedAt;
            return (
              <li key={v.id} className={s.visitRow}>
                <div className={s.visitTimeline}>
                  <div className={`${s.visitDot} ${inClinic ? s.visitDotActive : ""}`} />
                </div>
                <div className={s.itemBody}>
                  <div className={s.medTopRow}>
                    <span className={s.itemTitle}>
                      {fmtDate(v.visitedAt)} · {new Date(v.visitedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </span>
                    {inClinic
                      ? <span className={`${s.medStatus} ${s.medStatusActive}`}>In clinic</span>
                      : <span className={`${s.medStatus} ${s.medStatusEnded}`}>Returned</span>}
                  </div>
                  <div className={s.medMeta}>
                    {v.complaint && <span><strong>Complaint:</strong> {v.complaint}</span>}
                    {v.treatment && <span><strong>Treatment:</strong> {v.treatment}</span>}
                  </div>
                  {v.notes && <div className={s.itemNotes}>{v.notes}</div>}
                  {v.recordedByUsername && (
                    <div className={s.visitFooter}>
                      Logged {fmtRelative(v.visitedAt)} by {v.recordedByUsername}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
